import React, { CSSProperties } from 'react';
import { ContentParagraph, ContentParagraphModifier } from '../../ContentParagraph';
import Btn, { BtnModifier } from '../../../../components/molecules/Btn';
import { ContentHeadline } from '../../ContentHeadline';
import { Icons, SVG } from '../../../../components/atoms/SVG';
import { getHexColor } from '../../../../types/ICategory';
import { IConnectedPage } from '../../../../types/api/connectedPages/IConnectedPage';
import { PersonInformationList } from '../PersonInformationListItem';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { PersonInformationListItem } from '../PersonInformationListItem/PersonInformationListItem';
import { useHtmlValidId } from '../../../../hooks/useHtmlValidId';

type OrgChartListItemProperties = {
  page: IConnectedPage;
  isActive(page: IConnectedPage): boolean;
  depth: number;
  showDetails: boolean;
  onClick(page: IConnectedPage): void;
  secondLevelDedicatedOrgUnitVisualizationIndices?: number[];
};

export function OrgChartListItem({
  page,
  depth,
  showDetails,
  onClick,
  isActive,
}: OrgChartListItemProperties) {
  const { t } = useSonTranslation('ConnectedPageListItem');
  const id = useHtmlValidId();
  const level = depth + 2;

  const listItemChildrenId = `${id}-content`;

  const isWithinLineOrg = (item: IConnectedPage) =>
    !item.orgUnitVisualizationType || item.orgUnitVisualizationType === 'withinlineorganization';

  // We want to remove special representation child pages from the next level
  const childPagesWithinLineOrg = page.childPages?.filter(childPage => isWithinLineOrg(childPage));

  // horizontale Darstellung der zweiten Ebene
  // wäre auch für andere eben möglich, aber nicht für mehrere gleichzeitig
  const listStyle: CSSProperties =
    level === 2
      ? {
          display: 'grid',
          gridTemplateColumns: `repeat(${childPagesWithinLineOrg?.length || 1}, 1fr)`,
        }
      : {
          position: 'relative',
        };

  const orgClass = level === 2 ? 'org-chart-tree__root-node' : '';

  // Generate specialRepresentationItems
  const specialRepresentationItems: IConnectedPage[] = [];
  if (level === 2) {
    page.childPages?.filter(childPage =>
      childPage.orgUnitVisualizationType !== undefined,
    ).forEach(
      childPage => specialRepresentationItems.push(childPage),
    );
  }

  const isLeftItem = (item: IConnectedPage) => item.orgUnitVisualizationType?.includes('left');
  const isRightItem = (item: IConnectedPage) => item.orgUnitVisualizationType?.includes('right');

  const hasLeftSpecialRepresentationItems = specialRepresentationItems.some(item => isLeftItem(item));
  const hasRightSpecialRepresentationItems = specialRepresentationItems.some(item => isRightItem(item));
  const hasLeftItemsWithLine = specialRepresentationItems.some(item =>
    item.orgUnitVisualizationType?.includes('topleftwithline'),
  );
  const hasRightItemsWithLine = specialRepresentationItems.some(item =>
    item.orgUnitVisualizationType?.includes('toprightwithline'),
  );

  const generateListItemContainer = (item: IConnectedPage) => {
    const { pageLink, description, contactPersons = [], categories = [], childPages = [] } = item;

    const active = isActive(item);
    const expandButtonText = t(active ? 'closeFurtherLevels' : 'openFurtherLevels', {
      depth: depth ? depth + 2 : 2,
    });
    const hasChildren = childPagesWithinLineOrg && childPagesWithinLineOrg?.length > 0;
    const showChildrenButton = isWithinLineOrg(item) && hasChildren;
    const showContactPersons = showDetails && contactPersons.length > 0;
    const showSubOrganizations = showDetails && !isWithinLineOrg(item) && hasChildren;

    return (
      <article className="org-chart-tree__list-item-container" style={{ borderLeftColor: getHexColor(categories) }}>
        <ContentHeadline
          url={`${pageLink.url}${hasChildren ? '?mode=org' : ''}`}
          title={pageLink.name}
          modifier="top-headline"
        />
        <ContentParagraph modifier={ContentParagraphModifier.MARGIN_BOTTOM}>{description}</ContentParagraph>
        {showContactPersons
          ? (
              <div className="org-chart-tree__person-container">
                <PersonInformationList
                  headline={t('headlineContactPersons')}
                  className="org-chart-tree__person-container-list"
                >
                  {contactPersons?.map(person => (
                    <PersonInformationListItem
                      key={person.profileLink.url}
                      className="org-chart-tree__person-container-list-item"
                      person={person}
                    />
                  ))}
                </PersonInformationList>
              </div>
            )
          : null}
        {showSubOrganizations && (
          <p className="org-chart-tree__sub-organizations">
            <SVG svgClass="org-chart-tree__sub-organizations-icon" svgSpriteId={Icons.ORGANISATION} />
            {childPages?.length === 1
              ? t('subOrganizationsSingular')
              : t('subOrganizationsPlural', { count: childPages?.length })}
          </p>
        )}
        {showChildrenButton && (
          <span className="org-chart-tree__icon-container">
            <Btn
              onClick={() => onClick(page)}
              svgSpriteId={active ? Icons.ARROW_UP : Icons.ARROW_DOWN}
              ariaControls={listItemChildrenId}
              ariaExpanded={active}
              hiddenText
              modifier={BtnModifier.WHITE}
              text={expandButtonText}
              titleText={expandButtonText}
            />
          </span>
        )}
      </article>
    );
  };

  const generateSpecialRepresentationListItem = (item: IConnectedPage) => {
    const { orgUnitVisualizationType, pageLink } = item;
    const isWithLine = orgUnitVisualizationType?.includes('withline');

    return (
      <li
        key={`${pageLink.url}-special-representation`}
        className={`org-chart-tree__list-item${isWithLine ? ' org-chart-tree__org-with-line' : ''}`}
      >
        {generateListItemContainer(item)}
      </li>
    );
  };

  return (
    <li className="org-chart-tree__list-item">
      {level === 2
        ? (
            <div className={orgClass}>
              {hasLeftSpecialRepresentationItems && (
                <ul
                  className={`org-chart-tree__special-representation-left ${
                    hasLeftItemsWithLine ? 'org-chart-tree__special-representation-with-border' : ''
                  }`}
                >
                  {specialRepresentationItems.map(item =>
                    isLeftItem(item) ? generateSpecialRepresentationListItem(item) : null,
                  )}
                </ul>
              )}

              {generateListItemContainer(page)}

              {hasRightSpecialRepresentationItems && (
                <ul
                  className={`org-chart-tree__special-representation-right ${
                    hasRightItemsWithLine ? 'org-chart-tree__special-representation-with-border' : ''
                  }`}
                >
                  {specialRepresentationItems.map(item =>
                    isRightItem(item) ? generateSpecialRepresentationListItem(item) : null,
                  )}
                </ul>
              )}
            </div>
          )
        : (
            generateListItemContainer(page)
          )}

      <div id={listItemChildrenId} className="org-chart-tree__list-item-children" aria-hidden={!isActive(page)}>
        {childPagesWithinLineOrg && childPagesWithinLineOrg.length > 0 && isActive(page) && (
          <ul
            className={`org-chart-tree__level-${level}  org-chart-tree__child-count-${childPagesWithinLineOrg.length}`}
            style={listStyle}
          >
            {childPagesWithinLineOrg.map(childPage => (
              <OrgChartListItem
                key={`pageLink-${childPage.pageLink.url}`}
                page={childPage}
                isActive={isActive}
                onClick={onClick}
                showDetails={showDetails}
                depth={depth + 1}
              />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}
